/**
 * Vertical circulation — stair flights, railings, balconies, ladders.
 * owner: level
 */
import { chamferBox, stairFlight, xform, alignX, segLength, chamferCyl } from '../Geo.js';

/**
 * Stair flight. Origin is the bottom-front centre; the flight climbs toward
 * local +Z (rotated by rotY).
 */
export function stairs(w, {
  x, z, y = 0, rotY = 0, steps = 12, rise = 0.18, run = 0.30, width = 1.7,
  mat = 'concrete_floor', surface = 'concrete', stringer = 0, rail = null, nosing = 'concrete_wall',
}) {
  const g = stairFlight({ steps, rise, run, width, bevel: 0.03 });
  w.batch.add(g, mat, { matrix: xform([x, y, z], rotY), surface, uvScale: 1.1 });
  g.dispose();

  const cos = Math.cos(rotY), sin = Math.sin(rotY);
  const at = (u, v) => [x + cos * u + sin * v, z - sin * u + cos * v];

  // worn nosing strip on each tread — catches the light, reads as stone
  for (let i = 0; i < steps; i++) {
    const p = at(0, run * (i + 1) - 0.035);
    const n = chamferBox(width - 0.04, 0.04, 0.095, 0.018);
    w.batch.add(n, nosing, { matrix: xform([p[0], y + rise * (i + 1) + 0.005, p[1]], rotY), surface, uvScale: 0.5 });
    n.dispose();
    // collision, one box per tread
    const c = at(0, run * (i + 0.5));
    w.blocker(c[0], y + rise * (i + 1) / 2, c[1], width, rise * (i + 1), run, rotY, surface, false);
  }

  if (stringer) {
    const L = steps * run;
    const H = steps * rise;
    for (const s of (stringer === 2 ? [-1, 1] : [1])) {
      const p = at(s * (width / 2 + 0.12), L / 2);
      const g2 = chamferBox(0.24, H + 0.5, L, 0.04);
      w.batch.add(g2, 'concrete_wall', { matrix: xform([p[0], y + (H + 0.5) / 2 - 0.25, p[1]], rotY), surface: 'concrete', uvScale: 1.0 });
      g2.dispose();
      w.blocker(p[0], y + (H + 0.5) / 2 - 0.25, p[1], 0.24, H + 0.5, L, rotY, 'concrete');
    }
  }

  if (rail) {
    const sides = rail === 'both' ? [-1, 1] : [rail === 'left' ? -1 : 1];
    for (const s of sides) {
      const a = at(s * (width / 2 - 0.08), 0.1);
      const b = at(s * (width / 2 - 0.08), steps * run - 0.1);
      railing(w, {
        a: [a[0], y + 0.05, a[1]],
        b: [b[0], y + steps * rise, b[1]],
        height: 1.02, mat: 'metal_rusted',
      });
    }
  }
  return { topY: y + steps * rise, topZ: steps * run };
}

/** Post-and-rail railing along a (possibly sloped) segment. */
export function railing(w, { a, b, height = 1.05, mat = 'metal_rusted', surface = 'metal', post = 0.055, spacing = 1.15, mid = true, infill = 0 }) {
  const L = segLength(a, b);
  const n = Math.max(2, Math.round(L / spacing) + 1);
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    const p = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
    const g = chamferBox(post, height, post, 0.012);
    w.batch.add(g, mat, { matrix: xform([p[0], p[1] + height / 2, p[2]], 0), surface, uvScale: 0.4 });
    g.dispose();
  }
  const bar = (h, thick) => {
    const m = alignX([a[0], a[1] + h, a[2]], [b[0], b[1] + h, b[2]]);
    const g = chamferBox(L, thick, thick * 1.6, thick * 0.3);
    w.batch.add(g, mat, { matrix: m, surface, uvScale: 0.4 });
    g.dispose();
  };
  bar(height, 0.055);
  if (mid) bar(height * 0.52, 0.038);
  for (let i = 0; i < infill; i++) {
    const t = (i + 0.5) / infill;
    const p = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
    const g = chamferBox(0.022, height - 0.06, 0.022, 0.005);
    w.batch.add(g, mat, { matrix: xform([p[0], p[1] + height / 2, p[2]], 0), surface, uvScale: 0.3 });
    g.dispose();
  }
}

/** Cantilevered balcony / walkway slab with brackets and a railing. */
export function balcony(w, {
  x0, z0, x1, z1, y, thickness = 0.26, mat = 'concrete_floor', surface = 'concrete',
  rail = true, brackets = true, open = 'auto', railMat = 'metal_rusted',
}) {
  const X0 = Math.min(x0, x1), X1 = Math.max(x0, x1);
  const Z0 = Math.min(z0, z1), Z1 = Math.max(z0, z1);
  const wdt = X1 - X0, dep = Z1 - Z0;
  const g = chamferBox(wdt, thickness, dep, 0.04);
  w.batch.add(g, mat, { matrix: xform([(X0 + X1) / 2, y - thickness / 2, (Z0 + Z1) / 2], 0), surface, uvScale: 2.0 });
  g.dispose();
  w.blockerBox(X0, y - thickness, Z0, X1, y, Z1, surface, false);
  w.zone(X0, Z0, X1, Z1, y, surface);

  if (brackets) {
    const along = wdt > dep;
    const span = along ? wdt : dep;
    const n = Math.max(2, Math.round(span / 2.0));
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const px = along ? X0 + wdt * t : (X0 + X1) / 2;
      const pz = along ? (Z0 + Z1) / 2 : Z0 + dep * t;
      // brackets are spaced along the long axis and span the short one
      const bw = along ? 0.14 : wdt * 0.62;
      const bd = along ? dep * 0.62 : 0.14;
      const b = chamferBox(bw, 0.21, bd, 0.028);
      w.batch.add(b, 'concrete_wall', { matrix: xform([px, y - thickness - 0.10, pz], 0), surface: 'concrete', uvScale: 0.6 });
      b.dispose();
    }
  }
  if (rail) {
    const edges = open === 'auto'
      ? [[[X0, y, Z0], [X1, y, Z0]], [[X1, y, Z0], [X1, y, Z1]], [[X0, y, Z1], [X1, y, Z1]]]
      : open;
    for (const [a, b] of edges) railing(w, { a, b, height: 1.06, mat: railMat, infill: Math.round(segLength(a, b) / 0.22) });
  }
}

/** Rusted wall ladder to a roof — cheap verticality and great silhouette. */
export function ladder(w, { x, z, y0, y1, rotY = 0, mat = 'metal_rusted' }) {
  const h = y1 - y0;
  const cos = Math.cos(rotY), sin = Math.sin(rotY);
  for (const s of [-1, 1]) {
    const px = x + cos * (s * 0.22), pz = z - sin * (s * 0.22);
    const g = chamferCyl(0.028, h, 6, 0.006);
    w.batch.add(g, mat, { matrix: xform([px, y0 + h / 2, pz], 0), surface: 'metal', uvScale: 0.4 });
    g.dispose();
  }
  const rungs = Math.floor(h / 0.32);
  for (let i = 1; i < rungs; i++) {
    const g = chamferCyl(0.018, 0.44, 6, 0.005);
    g.rotateZ(Math.PI / 2);
    w.batch.add(g, mat, { matrix: xform([x, y0 + i * 0.32, z], rotY), surface: 'metal', uvScale: 0.3 });
    g.dispose();
  }
  // safety hoop at the top
  for (let i = 0; i < 3; i++) {
    const g = chamferCyl(0.36, 0.03, 12, 0.008, true);
    g.rotateX(Math.PI / 2);
    w.batch.add(g, mat, { matrix: xform([x, y1 - 0.4 - i * 0.7, z + 0.18], rotY), surface: 'metal', uvScale: 0.3 });
    g.dispose();
  }
}
